import { type LinearSubstep } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import LinearTile from "~/features/configurator/Linear/components/LinearTile";
import { Col, Row } from "~/common/components/Layout";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import { Card } from "~/features/configurator/Linear/components/Card";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { Alert } from "~/common/mui/Alert/Alert";
import {
  getTileData,
  makeRenderTile,
} from "~/features/configurator/common/components/GenericTileSubstep/GenericTileSubstep";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import type { RootState } from "~/app/store";
import type { CustomDispatch } from "~/app/store/types";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const CarriageLocatingFaceSubstep = ({ def, ...props }: Props) => {
  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          <Col size={4}>
            <SingleGuidewayTile def={def} />
          </Col>
        </Row>
      </div>

      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} className={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}
    </SubstepFrame>
  );
};
export default CarriageLocatingFaceSubstep;

interface TileProps {
  def: LinearSubstep;
}

const SingleGuidewayTile = ({ def }: TileProps) => {
  const t = useXmlConfiguratorTranslations().t!;
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const tileData = getTileData(def, dispatch)[0];

  const tileAbove = tileData[0];
  const tileBelow = tileData[1];

  const debugLanguage = isDebugLanguageEnabled();
  const renderTitle = makeRenderTile(debugLanguage, def);
  const kmats = useSelector(getKmats);

  const abovePicked = tileAbove.tileIndex === def.selectedTile;

  const locatingFaceImageUrl = t((abovePicked ? tileAbove : tileBelow).imageUrl);

  return (
    <Card disableHover={true}>
      <div className={"p-30"}>
        <Row>
          <Col size={12}>
            <LinearTile
              title={renderTitle(tileAbove)}
              isDisabled={!isTileEnabled(tileAbove, kmats)}
              isStandard={isTileStandard(tileAbove, kmats)}
              isPicked={abovePicked}
              forceStandardSpace={true}
              onClickHandler={tileAbove.onClick}
            />
          </Col>
        </Row>

        <div className={"my-3"}>
          <img className={"w-100"} src={locatingFaceImageUrl} />
        </div>

        {tileBelow && (
          <Row>
            <Col size={12}>
              <LinearTile
                title={renderTitle(tileBelow)}
                isDisabled={!isTileEnabled(tileBelow, kmats)}
                isStandard={isTileStandard(tileBelow, kmats)}
                isPicked={!abovePicked}
                forceStandardSpace={true}
                onClickHandler={tileBelow.onClick}
              />
            </Col>
          </Row>
        )}
      </div>
    </Card>
  );
};
